/**
 * The data gate: every DynamoDB / S3 need of the site goes through `POST /data-api/<op>` on the
 * site's own origin. The gate checks the Java session cookie and decides server-side what the
 * caller may read or write. No AWS credentials ever reach the browser.
 *
 * Operations (see awsRelated/data-gate/lambda/src/ops.ts): listByType, getItem, listOrgLists,
 * submitForm, createRequest, updateRequest, saveList, deleteList, adminPut, adminDelete,
 * adminBatchDelete, incrementView, uploadTicket, stageTicket, downloadUrl.
 */
import { refreshToken } from "store/middleware/api"

const GATE = "/data-api/"

export class GateError extends Error {
  constructor(status, code) {
    super(`gate ${status} ${code}`)
    this.status = status
    this.code = code
  }
}

/* CloudFront signs the request to the gate (SigV4) and needs the body hash in this header;
 * without it the edge answers 403 (which the SPA error page turns into index.html). */
async function sha256Hex(text) {
  const data = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest("SHA-256", data)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
}

/* Bodies above ~8 KB do not get through CloudFront to the gate (the edge refuses them and the
 * SPA error page answers "200 index.html"). Larger bodies are staged in the private bucket with a
 * presigned POST and the gate gets only their key. */
const STAGE_ABOVE_BYTES = 7 * 1024
const NOT_STAGEABLE = ["stageTicket", "uploadTicket"]

async function send(op, text) {
  const res = await fetch(GATE + op, {
    method: "POST",
    credentials: "include",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      "x-amz-content-sha256": await sha256Hex(text)
    },
    body: text
  })
  // The gate always answers JSON; anything else is CloudFront's error page, never a success.
  if (!(res.headers.get("content-type") ?? "").includes("application/json")) throw new GateError(res.status, "edge_error")
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) throw new GateError(res.status, payload?.error ?? "unknown")
  return payload
}

async function post(op, body) {
  const text = JSON.stringify(body ?? {})
  const size = new TextEncoder().encode(text).length
  if (size <= STAGE_ABOVE_BYTES || NOT_STAGEABLE.includes(op)) return send(op, text)
  const ticket = await send("stageTicket", JSON.stringify({ size }))
  const form = new FormData()
  Object.entries(ticket.fields ?? {}).forEach(([k, v]) => form.append(k, v))
  form.append("file", new Blob([text], { type: "application/json" }))
  const up = await fetch(ticket.url, { method: "POST", body: form })
  if (!up.ok) throw new GateError(up.status, "stage_upload_failed")
  return send(op, JSON.stringify({ $staged: ticket.key }))
}

/**
 * Calls one gate operation. A 401 `unauthenticated` means the Java session expired while the
 * browser still thinks it is signed in: refresh it the same way the API calls do, then retry once.
 */
export async function gateCall(op, body) {
  try {
    return await post(op, body)
  } catch (e) {
    if (e instanceof GateError && e.status === 401 && e.code === "unauthenticated" && isSignedIn()) {
      await refreshToken()
      return post(op, body)
    }
    throw e
  }
}

const isSignedIn = () => {
  try {
    return localStorage.getItem("isSignedIn") === "true"
  } catch (e) {
    return false
  }
}

/** The same rule as AuthGuard and the gate: any of these permissions makes the user an admin. */
const ADMIN_PERMISSIONS = ["WEBSHOP_ADMIN", "WEBSHOP_HR_ADMIN", "WEBSHOP_MARKETING_ADMIN"]
export const isGateAdmin = (state) => {
  const permissions = state?.auth?.userData?.role?.permissions
  return Array.isArray(permissions) && permissions.some((p) => ADMIN_PERMISSIONS.includes(p))
}

/** Reads every page of one type; the gate pages at 100 items. */
export async function listAllByType(type, onPage) {
  let cursor
  do {
    const page = await gateCall("listByType", { type, cursor })
    onPage(page.items ?? [], Boolean(page.cursor))
    cursor = page.cursor
  } while (cursor)
}

/**
 * Uploads one file with a presigned POST the gate issues. Returns what the record should store:
 * the public URL for content images and logos, the private key for a CV.
 */
export async function uploadFile(file, purpose) {
  const ticket = await gateCall("uploadTicket", {
    purpose,
    filename: file?.name ?? "file",
    contentType: file?.type || "application/octet-stream",
    size: file?.size ?? 0
  })
  const form = new FormData()
  Object.entries(ticket.fields ?? {}).forEach(([k, v]) => form.append(k, v))
  form.append("file", file)
  const res = await fetch(ticket.url, { method: "POST", body: form })
  if (!res.ok) throw new Error(`upload failed (${res.status})`)
  return ticket.publicUrl ?? ticket.key
}

/** A CV reference is either a legacy absolute URL or a private key the gate signs on demand. */
export const isPrivateFileKey = (ref) => typeof ref === "string" && /^(cv|orphan)\//.test(ref)

export async function openPrivateFile(key) {
  const { url } = await gateCall("downloadUrl", { key })
  window.open(url, "_blank", "noopener")
}

export const incrementView = (pk) => gateCall("incrementView", { pk }).catch(() => undefined)
